import { useEffect, useRef, useState } from 'react';
import type { Attachment } from '../types';
import * as db from '../lib/db';

function fileIcon(contentType: string | null): string {
  if (!contentType) return '📎';
  if (contentType.includes('pdf')) return '📄';
  if (contentType.startsWith('image/')) return '🖼️';
  return '📎';
}

export default function AttachmentsSection({ obligationId }: { obligationId: string }) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function refresh() {
    setLoading(true);
    try {
      setAttachments(await db.listAttachments(obligationId));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obligationId]);

  async function handleFilesSelected(files: FileList | null) {
    if (!files || !files.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await db.uploadAttachment(obligationId, file);
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleOpen(attachment: Attachment) {
    try {
      const url = await db.getAttachmentUrl(attachment.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleDelete(attachment: Attachment) {
    if (!confirm(`Delete "${attachment.file_name}"?`)) return;
    try {
      await db.deleteAttachment(attachment);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <section className="attachments-section">
      <div className="section-header">
        <h2>Documents</h2>
        <button
          type="button"
          className="btn-secondary small"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : '+ Add'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : attachments.length === 0 ? (
        <p className="muted empty">
          No documents yet. Add an insurance policy, registration, contract, or receipt.
        </p>
      ) : (
        <ul className="attachment-list">
          {attachments.map((a) => (
            <li key={a.id} className="attachment-row">
              <button type="button" className="attachment-open" onClick={() => handleOpen(a)}>
                <span className="emoji">{fileIcon(a.content_type)}</span>
                <span className="attachment-name">{a.file_name}</span>
              </button>
              <button
                type="button"
                className="attachment-delete"
                onClick={() => handleDelete(a)}
                aria-label={`Delete ${a.file_name}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
